import React, { useEffect, useMemo, useState } from 'react';

import useDepartmentStore from '../../stores/departmentStore';
import { useScope } from '../../context/PermissionContext';
import { DOCTORS_LOADING_COPY, DOCTORS_LOAD_ERROR_COPY } from '../../hooks/useAssignableProviders';
import { filterProvidersByDept } from '../visit/departmentResolution';
import { getTodayIST } from '../../utils/dateIST';
import BackdatedCompletionsLine from './BackdatedCompletionsLine';
import {
  DESK_BUCKET,
  DESK_BUCKET_LABELS,
  DESK_SUBCHIP,
  DESK_SUBCHIP_LABELS,
  ADMISSION_STATUS_FILTERS_MORE,
  ADMISSION_STATUS_LABELS,
} from '../../constants/admission';
import {
  NO_DEPARTMENT,
  deskDepartmentOptions,
  hasActiveFilters,
  defaultDeskFilters,
  dischargeWindowPreset,
  defaultDischargeWindowIST,
  validateDischargeRange,
  isDischargeWindowView,
} from './deskFilters';

/**
 * DeskFilterBar (bk-043) — controlled toolbar for Manage Admission, built to
 * the owner-finalized scheme (2026-07-15): two everyday BUCKETS ("is the
 * patient here?" / "have they left?") + work-queue SUB-CHIPS narrowing
 * Currently-admitted + money cross-cuts + a More… bucket holding all 11 raw
 * statuses. Purely driven by `{ filterState, onChange, counts }`; owns NO
 * state of its own.
 *
 * Sub-chips render ONLY while Currently admitted is the active bucket (S2
 * AC2) and toggle off back to the full bucket on a second click (Q1). Count
 * badges (S2 AC6) come from the ONE shared `computeDeskCounts` derivation
 * (container-computed `counts` prop) — a bucket/sub-chip the desk can't yet
 * count (index not warmed) renders with no badge, never a misleading 0.
 *
 * Chip + control backgrounds are solid in BOTH day and dark mode (button-
 * visibility rule) so an unselected chip never vanishes on a light page.
 *
 * admission-access (MOCK-4) — `showMoneyFilters` (the page passes the explicit
 * "holds BOTH VIEW_RUNNING_BILL + VIEW_IPD_DASHBOARD" boolean) gates the two
 * money cross-cuts, "With dues" and "⚠ Low deposit": for a viewer without it
 * (every doctor — primary included — and the nurse) they are not rendered at
 * all, since their counts would include rows that viewer may not see. Export
 * stays (it has no money columns). Defaults to shown = today's behaviour.
 *
 * admission-access S7-AC4/AC5 — `deptScoped` (the list response's `dept_scoped`:
 * the server's department wall applied to this viewer) narrows the Department
 * filter to the viewer's own departments, default "All my departments"; the
 * page passes the server-served assignable doctors as `doctors`
 * (`doctorsLoading` while they load — never the org roster in the meantime).
 * Defaults = today's behaviour.
 */
const chipBase = 'px-3 py-1.5 rounded-full text-[12.5px] font-medium border inline-flex items-center gap-1.5';
const chipOff = 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700';
const chipOn = 'bg-teal-600 border-teal-600 text-white';
const subchipBase = 'px-2.5 py-1 rounded-full text-[12px] font-medium border inline-flex items-center gap-1';
const subchipOff = 'bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700';
const subchipOn = 'bg-teal-600 border-teal-600 text-white';
const subchipAmberOff = 'bg-white dark:bg-slate-800 border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-900/30';
const subchipAmberOn = 'bg-amber-500 border-amber-500 text-white';
const moneyRedOff = 'bg-white dark:bg-slate-800 border-red-400 dark:border-red-600 text-red-800 dark:text-red-200 hover:bg-red-50 dark:hover:bg-red-900/30';
const moneyRedOn = 'bg-red-600 border-red-600 text-white';
const moneyAmberOff = 'bg-white dark:bg-slate-800 border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-200 hover:bg-amber-50 dark:hover:bg-amber-900/30';
const moneyAmberOn = 'bg-amber-500 border-amber-500 text-white';
const ctrlCls = 'px-2.5 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 text-[12.5px] focus:outline-none focus:ring-2 focus:ring-teal-500';

function CountBadge({ n, on }) {
  if (n == null) return null;
  return (
    <span className={`text-[11px] font-bold px-1.5 rounded-full leading-[16px] min-w-[17px] text-center ${on ? 'bg-white/25 text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-600'}`}>
      {n}
    </span>
  );
}

const DOCTOR_NOT_IN_LIST_COPY = 'Doctor not in this list';

export default function DeskFilterBar({ filterState, onChange, doctors = [], rowDoctors = [], rosterDoctors = [], doctorsLoading = false, doctorsError = false, onExport, exportDisabled, counts = {}, showMoneyFilters = true, deptScoped = false, backdatedCompleted = [] }) {
  const departments = useDepartmentStore((s) => s.departments);
  const deptsByMember = useDepartmentStore((s) => s.deptsByMember);
  const { viewerDeptIds } = useScope();
  const deptMenu = useMemo(
    () => deskDepartmentOptions({ departments, deptScoped, viewerDeptIds }),
    [departments, deptScoped, viewerDeptIds],
  );

  const f = filterState;
  const set = (patch) => onChange({ ...f, ...patch });

  // Assignable doctors (dept-narrowed) ∪, for EVERY viewer, every doctor on
  // the loaded rows (primary or care team) — narrowed by the selected department
  // to those on a row of it. Deduped by member id, alphabetical. A selected doctor
  // missing from both stays visible as an option (never a silent empty filter).
  const doctorOptions = useMemo(() => {
    const base = filterProvidersByDept(doctors, f.dept === NO_DEPARTMENT ? '' : f.dept, deptsByMember);
    const seen = new Set(base.map((d) => d.member_id));
    const deptKey = f.dept === NO_DEPARTMENT ? null : f.dept;
    const extras = !doctorsLoading
      ? rowDoctors.filter((d) => !seen.has(d.member_id) && (!f.dept || d.deptIds.has(deptKey)))
      : [];
    const all = [...base, ...extras];
    if (f.doctor && !all.some((d) => d.member_id === f.doctor)) {
      // Resolve the real name from the rows, the assignable list, then the org roster.
      const known = [rowDoctors, doctors, rosterDoctors].reduce((hit, list) => hit || list.find((d) => d.member_id === f.doctor), null);
      const knownName = known && (known.name || known.display_name);
      all.push(knownName ? { member_id: f.doctor, name: knownName } : { member_id: f.doctor, label: DOCTOR_NOT_IN_LIST_COPY });
    }
    return all.slice().sort((a, b) => String(a.name || a.display_name || '').localeCompare(String(b.name || b.display_name || '')));
  }, [doctors, rowDoctors, rosterDoctors, doctorsLoading, f.dept, f.doctor, deptsByMember]);

  const currentlyAdmittedActive = f.bucket === DESK_BUCKET.CURRENTLY_ADMITTED && !f.rawStatus;
  const dischargedActive = f.bucket === DESK_BUCKET.DISCHARGED && !f.rawStatus;
  // Owner click-through 2026-09-30 — the "Admitted from … to" control clutters
  // the Discharged window's own Today/Last 30 days/Custom presets. Hidden
  // (not cleared — from/to stay bookmarked) whenever the window view is
  // active; `applyDeskFilters` ignores them on this SAME view so a hidden
  // control can never silently narrow the list.
  const hideAdmittedRange = isDischargeWindowView(f);

  // discharge-perf F3 — Today / Last 30 days (default) / Custom dates. dfrom/
  // dto are SERVER params now (contract §1); every OTHER bucket/sub-chip/
  // status action below still clears them so switching away from (or back
  // into) Discharged never carries a stale discharge-date filter.
  const preset = dischargeWindowPreset(f);
  const [customOpen, setCustomOpen] = useState(preset === 'custom');
  // QA-FE-2 (fixed): seed the drafts from whatever window is ALREADY
  // committed (a bookmark, a reload, or the URL this component first mounts
  // with) — never a blank default. Lazy initializer covers fresh-open and
  // reload/bookmark; the effect below re-seeds on any LATER committed-window
  // change that isn't a local Apply echo (e.g. a search-pick widen while this
  // bar stays mounted), so Custom never shows stale/blank inputs and Apply
  // can never silently commit a different range than what's on screen.
  const [draftFrom, setDraftFrom] = useState(() => f.dfrom || '');
  const [draftTo, setDraftTo] = useState(() => f.dto || '');
  const [rangeError, setRangeError] = useState(null);
  useEffect(() => {
    if (f.dfrom && f.dto) {
      setDraftFrom(f.dfrom);
      setDraftTo(f.dto);
    }
  }, [f.dfrom, f.dto]);
  // Leaving the Discharged view resets the custom editor so re-entering
  // starts clean (mockup's default state); the committed dfrom/dto themselves
  // are cleared by selectDischarged/selectCurrentlyAdmitted etc. below.
  useEffect(() => {
    if (!dischargedActive) { setCustomOpen(false); setRangeError(null); }
  }, [dischargedActive]);
  const customOpenDisplay = dischargedActive && (customOpen || preset === 'custom');

  const selectCurrentlyAdmitted = () => set({
    bucket: DESK_BUCKET.CURRENTLY_ADMITTED, rawStatus: '', subchip: '', lowDepositOnly: false, withDuesOnly: false, dfrom: '', dto: '',
  });
  const selectDischarged = () => set({
    bucket: DESK_BUCKET.DISCHARGED, rawStatus: '', subchip: '', lowDepositOnly: false, withDuesOnly: false, dfrom: '', dto: '',
  });
  const toggleSubchip = (sub) => set({
    bucket: DESK_BUCKET.CURRENTLY_ADMITTED,
    rawStatus: '',
    subchip: f.subchip === sub ? '' : sub, // Q1: toggle-off back to the full bucket
    lowDepositOnly: false,
    withDuesOnly: false,
    dfrom: '',
    dto: '',
  });
  const toggleLowDeposit = () => set({
    bucket: DESK_BUCKET.CURRENTLY_ADMITTED, rawStatus: '', subchip: '', withDuesOnly: false,
    lowDepositOnly: !f.lowDepositOnly,
    dfrom: '',
    dto: '',
  });
  const toggleWithDues = () => set({
    bucket: DESK_BUCKET.DISCHARGED, rawStatus: '', subchip: '', lowDepositOnly: false,
    withDuesOnly: !f.withDuesOnly,
    dfrom: '',
    dto: '',
  });
  const selectMoreStatus = (s) => set({
    bucket: null, rawStatus: s, subchip: '', lowDepositOnly: false, withDuesOnly: false, dfrom: '', dto: '',
  });
  const selectPresetToday = () => {
    setCustomOpen(false);
    setRangeError(null);
    const today = getTodayIST();
    set({ dfrom: today, dto: today });
  };
  const selectPresetLast30 = () => {
    setCustomOpen(false);
    setRangeError(null);
    set({ dfrom: '', dto: '' }); // blank ⇒ server default (D4: last 30 days)
  };
  const openCustomRange = () => {
    const seed = (f.dfrom && f.dto) ? { from: f.dfrom, to: f.dto } : defaultDischargeWindowIST();
    setDraftFrom(seed.from);
    setDraftTo(seed.to);
    setRangeError(null);
    setCustomOpen(true);
  };
  const applyCustomRange = () => {
    const result = validateDischargeRange(draftFrom, draftTo);
    if (!result.valid) { setRangeError(result.message); return; }
    setRangeError(null);
    set({ dfrom: draftFrom, dto: draftTo });
  };

  const clearAll = () => onChange({ ...defaultDeskFilters() });

  return (
    <div data-testid="desk-filter-bar">
      <div className="flex flex-wrap gap-1.5 mb-3 items-start" role="group" aria-label="Status filter">
        {/* Bucket group: primary "Currently admitted" chip + its sub-chips read
            as ONE unit (tinted card) — sub-chips render ONLY while this bucket
            is active (S2 AC2). */}
        <div className="inline-flex flex-col gap-0 rounded-2xl border border-teal-200 dark:border-teal-800 bg-teal-50 dark:bg-teal-900/20 p-1.5">
          <div className="flex flex-wrap gap-1.5">
            <button
              type="button"
              onClick={selectCurrentlyAdmitted}
              data-testid="bucket-currently-admitted"
              className={`${chipBase} ${currentlyAdmittedActive ? chipOn : chipOff}`}
            >
              {DESK_BUCKET_LABELS.CURRENTLY_ADMITTED}
              <CountBadge n={counts.currentlyAdmitted} on={currentlyAdmittedActive} />
            </button>
          </div>
          {currentlyAdmittedActive && (
            <div className="flex flex-wrap items-center gap-1.5 mt-1.5 pl-2 border-l-2 border-teal-300 dark:border-teal-700 ml-1">
              <span className="text-[10.5px] font-bold uppercase tracking-wide text-teal-800 dark:text-teal-300 mr-0.5">Within</span>
              <button
                type="button"
                onClick={() => toggleSubchip(DESK_SUBCHIP.AWAITING_BED)}
                data-testid="filter-awaiting-bed"
                className={`${subchipBase} ${f.subchip === DESK_SUBCHIP.AWAITING_BED ? subchipAmberOn : subchipAmberOff}`}
              >
                {DESK_SUBCHIP_LABELS.AWAITING_BED}
                <CountBadge n={counts.awaitingBed} on={f.subchip === DESK_SUBCHIP.AWAITING_BED} />
              </button>
              <button
                type="button"
                onClick={() => toggleSubchip(DESK_SUBCHIP.BED_ASSIGNED)}
                data-testid="filter-bed-assigned"
                className={`${subchipBase} ${f.subchip === DESK_SUBCHIP.BED_ASSIGNED ? subchipOn : subchipOff}`}
              >
                {DESK_SUBCHIP_LABELS.BED_ASSIGNED}
                <CountBadge n={counts.bedAssigned} on={f.subchip === DESK_SUBCHIP.BED_ASSIGNED} />
              </button>
              <button
                type="button"
                onClick={() => toggleSubchip(DESK_SUBCHIP.DISCHARGE_IN_PROGRESS)}
                data-testid="filter-discharge-in-progress"
                className={`${subchipBase} ${f.subchip === DESK_SUBCHIP.DISCHARGE_IN_PROGRESS ? subchipOn : subchipOff}`}
              >
                {DESK_SUBCHIP_LABELS.DISCHARGE_IN_PROGRESS}
                <CountBadge n={counts.dischargeInProgress} on={f.subchip === DESK_SUBCHIP.DISCHARGE_IN_PROGRESS} />
              </button>
              <button
                type="button"
                onClick={() => toggleSubchip(DESK_SUBCHIP.NO_DOCTOR)}
                data-testid="filter-no-doctor"
                className={`${subchipBase} ${f.subchip === DESK_SUBCHIP.NO_DOCTOR ? subchipOn : subchipOff}`}
              >
                {DESK_SUBCHIP_LABELS.NO_DOCTOR}
                <CountBadge n={counts.noDoctor} on={f.subchip === DESK_SUBCHIP.NO_DOCTOR} />
              </button>
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={selectDischarged}
          data-testid="bucket-discharged"
          className={`${chipBase} ${dischargedActive ? chipOn : chipOff} self-start`}
        >
          {DESK_BUCKET_LABELS.DISCHARGED}
          <CountBadge n={counts.discharged} on={dischargedActive} />
        </button>

        {/* discharge-perf F3 — Today / Last 30 days (default) / Custom dates.
            dfrom/dto are now SERVER params (contract §1); "Last 30 days" is
            highlighted whenever dfrom/dto are blank OR equal what that
            default window resolves to (`dischargeWindowPreset`, single
            source — never a second inline comparison). */}
        {dischargedActive && (
          <div className="flex flex-col gap-1.5" data-testid="discharge-window-controls">
            <div className="flex flex-wrap gap-1.5 items-center" role="group" aria-label="Discharged date range">
              <button
                type="button"
                onClick={selectPresetToday}
                data-testid="filter-today-discharged"
                aria-pressed={preset === 'today'}
                className={`${chipBase} ${preset === 'today' ? chipOn : chipOff} self-start`}
              >
                Today
              </button>
              <button
                type="button"
                onClick={selectPresetLast30}
                data-testid="filter-last30-discharged"
                aria-pressed={preset === 'last30'}
                className={`${chipBase} ${preset === 'last30' ? chipOn : chipOff} self-start`}
              >
                Last 30 days
              </button>
              <button
                type="button"
                onClick={openCustomRange}
                data-testid="filter-custom-discharged"
                aria-pressed={preset === 'custom'}
                className={`${chipBase} ${preset === 'custom' ? chipOn : chipOff} self-start`}
              >
                Custom dates
              </button>
            </div>
            {preset === 'today' && <BackdatedCompletionsLine items={backdatedCompleted} testId="desk-backdated" />}
            {customOpenDisplay && (
              <div className="flex flex-wrap items-center gap-1.5" data-testid="discharge-custom-range">
                <label className="text-[12px] text-slate-500 dark:text-slate-400">From</label>
                <input
                  type="date"
                  className={ctrlCls}
                  value={draftFrom}
                  max={draftTo || undefined}
                  onChange={(e) => setDraftFrom(e.target.value)}
                  aria-label="Discharged from"
                  data-testid="discharge-custom-from"
                />
                <label className="text-[12px] text-slate-500 dark:text-slate-400">to</label>
                <input
                  type="date"
                  className={ctrlCls}
                  value={draftTo}
                  min={draftFrom || undefined}
                  onChange={(e) => setDraftTo(e.target.value)}
                  aria-label="Discharged to"
                  data-testid="discharge-custom-to"
                />
                <button
                  type="button"
                  onClick={applyCustomRange}
                  data-testid="discharge-custom-apply"
                  className="px-3 py-1.5 rounded-lg text-[12.5px] font-medium border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/40 text-teal-800 dark:text-teal-200 hover:bg-teal-100 dark:hover:bg-teal-900/50"
                >
                  Apply
                </button>
              </div>
            )}
            {rangeError && (
              <div
                role="alert"
                data-testid="discharge-range-error"
                className="inline-block text-[12px] font-medium text-red-800 dark:text-red-200 bg-red-50 dark:bg-red-900/40 border border-red-400 dark:border-red-600 rounded-lg px-2.5 py-1.5"
              >
                {rangeError}
              </div>
            )}
          </div>
        )}

        <span className="w-px self-stretch bg-slate-300 dark:bg-slate-600 mx-1" aria-hidden="true" />

        {showMoneyFilters && (
          <>
            <button
              type="button"
              onClick={toggleWithDues}
              data-testid="filter-with-dues"
              className={`${chipBase} ${f.withDuesOnly ? moneyRedOn : moneyRedOff} self-start`}
            >
              With dues
            </button>
            <button
              type="button"
              onClick={toggleLowDeposit}
              data-testid="filter-low-deposit"
              className={`${chipBase} ${f.lowDepositOnly ? moneyAmberOn : moneyAmberOff} self-start`}
            >
              ⚠ Low deposit
            </button>
          </>
        )}

        <select
          className={`${ctrlCls} rounded-full self-start`}
          value={f.rawStatus || ''}
          onChange={(e) => { if (e.target.value) selectMoreStatus(e.target.value); }}
          aria-label="More statuses"
          data-testid="filter-more-statuses"
        >
          <option value="">More…</option>
          {ADMISSION_STATUS_FILTERS_MORE.map((s) => (
            <option key={s} value={s}>{ADMISSION_STATUS_LABELS[s] || s}</option>
          ))}
        </select>
      </div>

      {/* Secondary row — doctor / dept / date range / clear / export */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <select
          className={ctrlCls}
          value={f.doctor}
          onChange={(e) => set({ doctor: e.target.value })}
          aria-label="Doctor filter"
        >
          <option value="">All doctors</option>
          {doctorsLoading && <option value="" disabled>{DOCTORS_LOADING_COPY}</option>}
          {doctorOptions.map((d) => (
            <option key={d.member_id} value={d.member_id}>
              {d.label || `Dr. ${String(d.name || d.display_name || d.member_id).replace(/^dr\.?\s+/i, '')}`}
            </option>
          ))}
        </select>
        {doctorsError && (
          <span className="text-[11px] text-slate-500 dark:text-slate-400" data-testid="doctor-filter-error">{DOCTORS_LOAD_ERROR_COPY}</span>
        )}

        <select
          className={ctrlCls}
          value={f.dept}
          onChange={(e) => set({ dept: e.target.value })}
          aria-label="Department filter"
        >
          <option value="">{deptMenu.allLabel}</option>
          {deptMenu.departments.map((d) => (
            <option key={d.dept_id || d.id} value={d.dept_id || d.id}>{d.name}</option>
          ))}
          <option value={NO_DEPARTMENT}>No department</option>
        </select>

        {!hideAdmittedRange && (
          <>
            <label className="text-[12px] text-slate-500 dark:text-slate-400">Admitted from</label>
            <input
              type="date"
              className={ctrlCls}
              value={f.from}
              max={f.to || undefined}
              onChange={(e) => set({ from: e.target.value })}
              aria-label="Admitted from"
            />
            <label className="text-[12px] text-slate-500 dark:text-slate-400">to</label>
            <input
              type="date"
              className={ctrlCls}
              value={f.to}
              min={f.from || undefined}
              onChange={(e) => set({ to: e.target.value })}
              aria-label="Admitted to"
            />
          </>
        )}

        {hasActiveFilters(f) && (
          <button
            type="button"
            onClick={clearAll}
            data-testid="clear-filters"
            className="px-3 py-1.5 rounded-lg text-[12.5px] font-medium border border-teal-400 dark:border-teal-600 bg-teal-50 dark:bg-teal-900/30 text-teal-800 dark:text-teal-200 hover:bg-teal-100 dark:hover:bg-teal-900/50"
          >
            Clear filters
          </button>
        )}

        <span className="flex-1" />

        <button
          type="button"
          onClick={onExport}
          disabled={exportDisabled}
          data-testid="export-csv"
          title={exportDisabled ? 'Nothing to export' : 'Export the filtered rows as CSV'}
          className="px-3 py-1.5 rounded-lg text-[12.5px] font-medium border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-60"
        >
          ⭳ Export
        </button>
      </div>
    </div>
  );
}
